/**
 * EventStageBar — compact horizontal lifecycle stage progress strip.
 * Pure display component: no API calls, no side effects.
 *
 * Props:
 *   stages     [{id, label}]    ordered forward-path stage definitions
 *   current    string           current stage id
 *   terminal   {id, label}?     optional off-path terminal (CANCELLED, CLOSED_LOST, LOST)
 *   accentHex  string           active/completed accent color (default: indigo #6366F1)
 */
import React from "react";
import { Check } from "lucide-react";

export function EventStageBar({ stages = [], current, terminal, accentHex = "#6366F1" }) {
  const currentIdx = stages.findIndex(s => s.id === current);
  const isTerminal = terminal && current === terminal.id;

  return (
    <div className="overflow-x-auto scrollbar-none" data-testid="event-stage-bar">
      <div className="flex items-center min-w-max px-4 py-2">
        {stages.map((s, idx) => {
          const done   = !isTerminal && idx < currentIdx;
          const active = !isTerminal && s.id === current;

          return (
            <React.Fragment key={s.id}>
              {/* Stage node */}
              <div
                className="flex flex-col items-center"
                data-testid={`stage-node-${s.id}`}
              >
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border-2 transition-all"
                  style={{
                    background:  done || active ? accentHex : "transparent",
                    borderColor: done || active ? accentHex : "#D1D5DB",
                    color:       done || active ? "#fff"    : "#9CA3AF",
                  }}
                >
                  {done ? <Check size={9} strokeWidth={3} /> : idx + 1}
                </div>
                <span
                  className="mt-0.5 text-[9px] leading-none font-medium whitespace-nowrap"
                  style={{
                    color: active
                      ? accentHex
                      : done
                      ? "#374151"
                      : "#9CA3AF",
                  }}
                >
                  {s.label}
                </span>
              </div>

              {/* Connector line */}
              {idx < stages.length - 1 && (
                <div
                  className="self-start mt-2.5 flex-shrink-0"
                  style={{
                    width:      "28px",
                    height:     "2px",
                    background: done ? accentHex : "#E5E7EB",
                    margin:     "0 2px",
                  }}
                />
              )}
            </React.Fragment>
          );
        })}

        {/* Terminal (off-path) stage */}
        {terminal && (
          <>
            <div
              className="self-start flex-shrink-0 mt-2.5"
              style={{ width: "16px", height: "2px", background: "#E5E7EB", margin: "0 4px 0 8px" }}
            />
            <div className="flex flex-col items-center" data-testid={`stage-node-${terminal.id}`}>
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border-2"
                style={{
                  background:  isTerminal ? "#FEE2E2" : "transparent",
                  borderColor: isTerminal ? "#F87171" : "#E5E7EB",
                  color:       isTerminal ? "#DC2626" : "#D1D5DB",
                }}
              >
                {isTerminal ? "✕" : "—"}
              </div>
              <span
                className="mt-0.5 text-[9px] leading-none font-medium whitespace-nowrap"
                style={{ color: isTerminal ? "#DC2626" : "#D1D5DB" }}
              >
                {terminal.label}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
